import React, { useCallback, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { StrapiUserTypeRow } from '../../api/strapiUserTypes';
import { useAuth } from '../../auth/AuthContext';
import { UserTypesDataGrid } from '../../components/UserTypesDataGrid';
import { appDocumentTitle } from '../../appMeta';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { PageShell } from '../../layout/PageShell';
import {
  useCreateUserTypeMutation,
  useDeleteUserTypeMutation,
  useUpdateUserTypeMutation,
  useUserTypesQuery,
} from '../../query/hooks';

function emptyForm() {
  return { type_code: '', type_name: '', description: '' };
}

/** Superadmin: user type master at `/superadmin/user-types`. */
export function SuperadminUserTypesPage() {
  useDocumentTitle(appDocumentTitle('User type'));
  const { jwt } = useAuth();

  const { data: rows = [], isPending, isError, error } = useUserTypesQuery(jwt);
  const createMut = useCreateUserTypeMutation(jwt);
  const updateMut = useUpdateUserTypeMutation(jwt);
  const deleteMut = useDeleteUserTypeMutation(jwt);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<'create' | 'edit'>('create');
  const [editingDocumentId, setEditingDocumentId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);

  const [pendingDelete, setPendingDelete] = useState<StrapiUserTypeRow | null>(null);

  const openDelete = useCallback(
    (row: StrapiUserTypeRow) => {
      deleteMut.reset();
      setPendingDelete(row);
    },
    [deleteMut],
  );

  const closeDelete = useCallback(() => {
    if (!deleteMut.isPending) {
      deleteMut.reset();
      setPendingDelete(null);
    }
  }, [deleteMut]);

  const openCreate = useCallback(() => {
    setEditorMode('create');
    setEditingDocumentId(null);
    setForm(emptyForm());
    setFormError(null);
    setEditorOpen(true);
  }, []);

  const openEdit = useCallback((row: StrapiUserTypeRow) => {
    if (!row.documentId) {
      setFormError('This row has no document id; refresh and try again.');
      return;
    }
    setEditorMode('edit');
    setEditingDocumentId(row.documentId);
    setForm({
      type_code: row.type_code,
      type_name: row.type_name,
      description: row.description ?? '',
    });
    setFormError(null);
    setEditorOpen(true);
  }, []);

  const closeEditor = useCallback(() => {
    setEditorOpen(false);
    setFormError(null);
  }, []);

  const gridCallbacks = useMemo(
    () => ({
      onEdit: openEdit,
      onDelete: openDelete,
    }),
    [openEdit, openDelete],
  );

  async function onSubmitEditor(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    const code = form.type_code.trim();
    const name = form.type_name.trim();
    if (!code || !name) {
      setFormError('Code and name are required.');
      return;
    }
    if (!/^[a-z][a-z0-9_]*$/i.test(code)) {
      setFormError('Use letters, numbers, and underscores only; start with a letter.');
      return;
    }
    const description = form.description.trim();
    try {
      if (editorMode === 'create') {
        await createMut.mutateAsync({ type_code: code, type_name: name, description });
      } else if (editingDocumentId) {
        await updateMut.mutateAsync({
          documentId: editingDocumentId,
          type_code: code,
          type_name: name,
          description,
        });
      }
      closeEditor();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Request failed.');
    }
  }

  async function confirmDelete() {
    if (!pendingDelete?.documentId) return;
    try {
      await deleteMut.mutateAsync(pendingDelete.documentId);
      deleteMut.reset();
      setPendingDelete(null);
    } catch {
      /* surfaced in dialog */
    }
  }

  return (
    <PageShell
      title="User types"
      actions={
        <Tooltip title="Add user type">
          <IconButton
            color="primary"
            onClick={openCreate}
            aria-label="Add user type"
            data-testid="user-types-add-open"
            size="small"
            sx={{ bgcolor: 'action.hover' }}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
      }
    >
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <UserTypesDataGrid
          rows={rows}
          isLoading={isPending}
          isError={isError}
          error={error ?? undefined}
          callbacks={gridCallbacks}
        />
      </Box>

      <Dialog open={editorOpen} onClose={closeEditor} fullWidth maxWidth="sm" aria-labelledby="user-type-editor-title">
        <DialogTitle id="user-type-editor-title">
          {editorMode === 'create' ? 'New user type' : 'Edit user type'}
        </DialogTitle>
        <Box component="form" onSubmit={onSubmitEditor}>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              <TextField
                label="Code"
                value={form.type_code}
                onChange={(ev) => setForm((f) => ({ ...f, type_code: ev.target.value }))}
                required
                fullWidth
                size="small"
                disabled={editorMode === 'edit'}
                helperText="Stable identifier (e.g. marketing_pic). Cannot change after create."
                inputProps={{ 'data-testid': 'user-type-form-code' }}
              />
              <TextField
                label="Name"
                value={form.type_name}
                onChange={(ev) => setForm((f) => ({ ...f, type_name: ev.target.value }))}
                required
                fullWidth
                size="small"
                inputProps={{ 'data-testid': 'user-type-form-name' }}
              />
              <TextField
                label="Description"
                value={form.description}
                onChange={(ev) => setForm((f) => ({ ...f, description: ev.target.value }))}
                fullWidth
                size="small"
                multiline
                minRows={2}
              />
              {formError && (
                <Typography variant="body2" color="error">
                  {formError}
                </Typography>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button type="button" onClick={closeEditor}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={createMut.isPending || updateMut.isPending}
              data-testid="user-type-form-submit"
            >
              {editorMode === 'create'
                ? createMut.isPending
                  ? 'Creating…'
                  : 'Create'
                : updateMut.isPending
                  ? 'Saving…'
                  : 'Save'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={Boolean(pendingDelete)} onClose={closeDelete} maxWidth="xs" fullWidth>
        <DialogTitle>Delete user type?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {pendingDelete ? (
              <>
                Remove <strong>{pendingDelete.type_name}</strong> ({pendingDelete.type_code})? Users assigned to this
                type must be reassigned first or deletion will fail.
              </>
            ) : null}
          </Typography>
          {deleteMut.isError && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {deleteMut.error instanceof Error ? deleteMut.error.message : 'Delete failed.'}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDelete} disabled={deleteMut.isPending}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={confirmDelete} disabled={deleteMut.isPending}>
            {deleteMut.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
